import {
  DOCUMENT_EVIDENCE_PATH,
  redactVisibleDocumentEvidencePaths,
} from "@repo/shared/document-evidence-link";
import ReactMarkdown from "react-markdown";
import {
  rehypeDocumentEvidenceRuns,
  renderDocumentEvidenceLink,
} from "@/components/ui/message-markdown-evidence";
import {
  hasMessageHighlight,
  rehypeMessageHighlights,
} from "@/components/ui/message-markdown-highlight";
import { renderMessageMentionLink } from "@/components/ui/message-markdown-mention";
import {
  useOptionalMentionInteraction,
  useReferencePresentationResolver,
} from "@/features/rich-text/mentions";
import { classifyDocumentEvidenceHref } from "@/lib/document-evidence-link";
import {
  canonicalizeMessageMentionAnnotations,
  MESSAGE_MENTION_PATH,
  parseMessageMentionHref,
} from "@/lib/message-mention-link";
import { cn } from "@/lib/utils";

interface InlineReferenceMarkdownProps {
  content: string;
  className?: string;
  /** Authored markdown opts in; plain-text questions retain literal punctuation by default. */
  formatting?: "literal" | "markdown";
  /**
   * `static` renders mention chips without a role, tab stop or activation — for text that
   * already sits inside a clickable host, such as an ask-user option button.
   */
  mentions?: "interactive" | "static";
}

export function InlineReferenceMarkdown({
  content,
  className,
  mentions = "interactive",
  formatting = "literal",
}: InlineReferenceMarkdownProps) {
  const mentionInteraction = useOptionalMentionInteraction();
  const resolveReferencePresentation = useReferencePresentationResolver();

  // A legacy `@Label [df-mention type=… id=…]` annotation is repaired to canonical mention
  // markdown before anything else looks at the text, so both syntaxes take one path (LAT-895).
  const canonicalContent = canonicalizeMessageMentionAnnotations(content);

  // Plain-text questions preserve literal punctuation unless they carry reference/highlight
  // markup. Authored Markdown opts into parsing even when it contains no references.
  if (
    formatting === "literal" &&
    !canonicalContent.includes(DOCUMENT_EVIDENCE_PATH) &&
    !canonicalContent.includes(MESSAGE_MENTION_PATH) &&
    !hasMessageHighlight(canonicalContent)
  ) {
    return <span className={cn("inline-reference-markdown", className)}>{content}</span>;
  }

  // Only a Markdown link can become a chip. A reserved path written as prose or inside
  // inline code is model-authored transport detail and must never reach the reader.
  const safeContent = redactVisibleDocumentEvidencePaths(canonicalContent);

  return (
    <span className={cn("inline-reference-markdown", className)}>
      <ReactMarkdown
        // `mark` is on the list because the highlight transform creates it: everything else
        // a question may parse into still unwraps to its text.
        allowedElements={
          formatting === "markdown"
            ? ["p", "a", "mark", "strong", "em", "code", "del"]
            : ["p", "a", "mark"]
        }
        unwrapDisallowed
        skipHtml
        // The same transforms the transcript uses: a question that cites two adjacent
        // passages reads as one marker here too, and `==TBD==` reads as a highlight.
        rehypePlugins={[rehypeDocumentEvidenceRuns, rehypeMessageHighlights]}
        components={{
          p: ({ children }) => <>{children}</>,
          a: ({ href, children, node }) => {
            const resolvedHref = href ?? "";
            if (classifyDocumentEvidenceHref(resolvedHref).kind !== "unrelated") {
              // Ask-user surfaces have no per-surface capability flag: a question that cites
              // evidence always renders it. The marker is a button, though, so inside a
              // clickable host it degrades to the label the model wrote rather than nesting
              // one interactive inside another.
              return renderDocumentEvidenceLink({
                children,
                enabled: mentions === "interactive",
                href: resolvedHref,
                node,
              });
            }

            const mention = parseMessageMentionHref(resolvedHref);
            if (mention) {
              return renderMessageMentionLink({
                children,
                interactive: mentions === "interactive",
                mention,
                mentionInteraction,
                resolveReferencePresentation,
              });
            }

            return <>{children}</>;
          },
        }}
      >
        {safeContent}
      </ReactMarkdown>
    </span>
  );
}
